import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { beautyAutoLine, boldAutoColumn } from "../../../../utils/pdf/page";
import { buildAutoTable } from "../../../../utils/pdf/buildAutoTable";
import { PDFLandscapeScheduleConstants } from "../../../../utils/static/pdfConstants";
import {
  buildAgreementLabel,
  createExchangeRateResolver,
  DEFAULT_UNABSORBED_SUMMARY,
} from "../shared";
import { getIrasTableStyles } from "./tableSpacing";
import {
  allocateIrasUnabsorbedSgdAmounts,
  getUnabsorbedSummaryAllocation,
} from "./unabsorbedSgdAllocation";

export const renderIrasUnabsorbedSummary = ({
  company,
  currencyView,
  currencyRate,
  unabsorbedSgdSummary,
  pdf,
  tableSpacingOptions,
  unabsorbedSummary = DEFAULT_UNABSORBED_SUMMARY,
}) => {
  const hasUnutilisedSummary =
    !pdf.isExcel &&
    company?.taxPeriodType !== "cross_ya" &&
    company?.companyType !== "investment" &&
    ((unabsorbedSummary.caRows || []).length > 0 ||
      (unabsorbedSummary.lossRows || []).length > 0 ||
      (unabsorbedSummary.donationRows || []).length > 0);

  if (!hasUnutilisedSummary) return;

  const totalSummaryLines = [];
  const totalSummaryDoubleLines = [];

  const lsc = PDFLandscapeScheduleConstants;
  pdf.doc.addPage("a4", "landscape");
  pdf.isLandscape = true;
  pdf.constants = lsc;
  pdf.rightX = lsc.rightX;

  const isNonSGD = currencyView !== "SGD";
  const exchangeRate = currencyRate ?? company?.currencyRate ?? 1;
  const getExchangeRate = createExchangeRateResolver(exchangeRate);
  const baseHeaders = [
    "",
    "Balance Available",
    "Current Year Utilisation",
    "Balance c/f",
  ];
  const headers = isNonSGD
    ? [
        ...baseHeaders,
        "Exchange Rate",
        "Balance Available",
        "Current Year Utilisation",
        "Balance c/f",
      ]
    : baseHeaders;

  const body = [
    [
      "",
      boldAutoColumn(currencyView),
      boldAutoColumn(currencyView),
      boldAutoColumn(currencyView),
      ...(isNonSGD
        ? [
            "",
            boldAutoColumn("SGD"),
            boldAutoColumn("SGD"),
            boldAutoColumn("SGD"),
          ]
        : []),
    ],
  ];
  const disregardedLabel = "Disregarded";

  const pushSectionFromSummary = (typeLabel, rows = [], sgdSummary) => {
    if (!rows || rows.length === 0) return;

    body.push([boldAutoColumn(typeLabel)]);

    let totalBf = 0;
    let totalUtil = 0;
    let totalCf = 0;
    let totalSgdBf = 0;
    let totalSgdUtil = 0;
    let totalSgdCf = 0;

    const sgdAllocations = isNonSGD
      ? allocateIrasUnabsorbedSgdAmounts({ rows, currencyRate: exchangeRate, summary: sgdSummary })
      : [];

    rows.forEach((row, rowIndex) => {
      const {
        balanceAvailable: bf,
        currentYearUtilisation: util,
        balanceCf: cf
      } = getUnabsorbedSummaryAllocation(row);
      const isForfeited = Boolean(row.isForfeited);
      const rate = getExchangeRate(row.exchangeRate);
      const { balanceAvailable: sgdBf, balanceCf: sgdCf } = sgdAllocations[rowIndex] ?? {};
      const sgdUtilised = sgdCf - sgdBf;

      if (!isForfeited) {
        totalBf += bf;
        totalUtil += Math.abs(util);
        totalCf += cf;

        if (isNonSGD) {
          totalSgdBf += sgdBf;
          totalSgdUtil += sgdUtilised;
          totalSgdCf += sgdCf;
        }
      }

      const rowData = [
        (typeLabel === "Capital Allowances"
          ? "YA " + row.yearOfAssessment
          : row.yearOfAssessment) + buildAgreementLabel(row),
        formatAmount(bf),
        isForfeited
          ? disregardedLabel
          : formatAmount(util, true, true),
        isForfeited ? disregardedLabel : formatAmount(cf),
      ];

      if (isNonSGD) {
        if (isForfeited) {
          rowData.push(
            "",
            formatAmount(sgdBf),
            disregardedLabel,
            disregardedLabel
          );
        } else {
          rowData.push(
            `@ ${rate.toFixed(4)}`,
            formatAmount(sgdBf),
            formatAmount(sgdUtilised),
            formatAmount(sgdCf)
          );
        }
      }

      body.push(rowData);
    });

    if (totalBf !== 0 || totalUtil !== 0 || totalCf !== 0) {
      const totalRow = [
        boldAutoColumn("Total"),
        formatAmount(totalBf),
        formatAmount(totalUtil, true, true),
        formatAmount(totalCf),
      ];

      if (isNonSGD) {
        totalRow.push(
          "",
          formatAmount(totalSgdBf),
          formatAmount(totalSgdUtil, true, true),
          formatAmount(totalSgdCf)
        );
      }

      body.push(totalRow);
      totalSummaryLines.push(body.length - 2);
      totalSummaryLines.push(body.length - 1);
      totalSummaryDoubleLines.push(body.length - 1);

      if (typeLabel !== "Donations") body.push([]);
    }
  };

  pushSectionFromSummary("Capital Allowances", unabsorbedSummary.caRows, unabsorbedSgdSummary?.caRows);
  pushSectionFromSummary("Losses", unabsorbedSummary.lossRows, unabsorbedSgdSummary?.lossRows);
  pushSectionFromSummary("Donations", unabsorbedSummary.donationRows, unabsorbedSgdSummary?.donationRows);

  const payload = isNonSGD ? 1 : 2;
  const lineColumns = isNonSGD ? [1, 2, 3, 5, 6, 7] : [1, 2, 3];
  const colStyles = {
    0: { halign: "left", cellWidth: 40 * payload },
    1: { halign: "right", valign: "bottom", cellWidth: 32 * payload },
    2: { halign: "right", valign: "bottom", cellWidth: 32 * payload },
    3: { halign: "right", valign: "bottom", cellWidth: 32 * payload },
  };

  if (isNonSGD) {
    colStyles[4] = { halign: "center", cellWidth: 24 };
    colStyles[5] = { halign: "right", valign: "bottom", cellWidth: 32 };
    colStyles[6] = { halign: "right", valign: "bottom", cellWidth: 32 };
    colStyles[7] = { halign: "right", valign: "bottom", cellWidth: 32 };
  }

  buildAutoTable({
    pdf,
    customStartY: 55,
    schedule: {
      name: "Unutilised Items Brought Forward / Carried Forward",
      subTitle: "Statement A1",
    },
    head: [
      headers.map((header) => ({
        content: header,
        styles: { fontStyle: "bold" },
      })),
    ],
    company,
    body,
    theme: "plain",
    styles: getIrasTableStyles(pdf, tableSpacingOptions),
    headStyles: { fontStyle: "bold", halign: "center" },
    columnStyles: colStyles,
    didParseCell: function (data) {
      if (
        [0].includes(data.row.index) &&
        lineColumns.includes(data.column.index)
      ) {
        data.cell.styles.halign = "center";
      }
    },
    didDrawCell: function (data) {
      beautyAutoLine(
        data,
        pdf.doc,
        totalSummaryLines.includes(data.row.index) &&
          lineColumns.includes(data.column.index),
        totalSummaryDoubleLines.includes(data.row.index) &&
          lineColumns.includes(data.column.index),
        data.cell.width * 0.98
      );
    },
    lineSettings: {
      totalLines: totalSummaryLines,
      totalDoubleLines: totalSummaryDoubleLines,
      totalRows: lineColumns,
    },
  });
};
